import React, { useEffect, useState } from "react";
import { Image as ImageIcon, ImageOff, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { buildSemanticQuery } from "@/lib/widgetCatalog";

// Batch 9: enterprise Image widget — static URL/upload OR dynamic binding to a dataset
// field (image URL column, reacts to dashboard filters), crop/zoom with focal point,
// hover effects and click actions (URL / dashboard navigation). Title/Subtitle/
// Description(i) + fonts come from the shared WidgetCard header.
export function ImageWidget({ config, globalFilters = [], publicToken = null }) {
  const img = (config || {}).image || {};
  const dynamic = img.source === "field" && img.dataset_id && img.field;
  const [dynUrl, setDynUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hov, setHov] = useState(false);

  const filterKey = JSON.stringify(globalFilters || []);
  useEffect(() => {
    let active = true;
    if (!dynamic) { setDynUrl(null); return; }
    const run = async (withFilters) => {
      const semQuery = buildSemanticQuery({ dimensions: [{ field: img.field }], measures: [], limit: 1 }, withFilters ? globalFilters : []);
      const { data } = publicToken
        ? await api.post("/public/model/query", { token: publicToken, dataset_id: img.dataset_id, semantic_query: semQuery }, { timeout: 30000 })
        : await api.post(`/datasets/${img.dataset_id}/model/query`, semQuery, { timeout: 30000 });
      return data?.rows?.[0]?.[img.field] ?? null;
    };
    setLoading(true);
    run(true)
      .catch(() => run(false))
      .then((u) => { if (active) setDynUrl(u ? String(u) : null); })
      .catch(() => { if (active) setDynUrl(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamic, img.dataset_id, img.field, filterKey, publicToken]);

  const url = dynamic ? dynUrl : img.url;
  const action = img.action || (img.link ? { type: "url", url: img.link } : null);
  const hasAction = action && action.type && action.type !== "none"
    && (action.type === "url" ? !!action.url : action.type === "dashboard" ? !!action.dashboard_id && !publicToken : false);
  const runAction = (e) => {
    if (!hasAction) return;
    e.stopPropagation();
    if (action.type === "url") window.open(action.url, "_blank", "noopener");
    else if (action.type === "dashboard") window.location.assign(`/builder/${action.dashboard_id}`);
  };

  if (loading) {
    return (
      <div data-testid="image-widget-loading" className="h-full w-full flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)] opacity-60" />
      </div>
    );
  }
  if (!url) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div data-testid="image-widget-empty" className="text-center text-[color:var(--ink-3)] text-sm px-4">
          {dynamic ? <ImageOff className="w-6 h-6 mx-auto mb-2 opacity-60" /> : <ImageIcon className="w-6 h-6 mx-auto mb-2 opacity-60" />}
          <div className="font-medium text-[color:var(--ink-2)]">{dynamic ? "No image for the current data" : "Add an image"}</div>
          <div className="text-xs mt-1">{dynamic ? "The bound field returned no URL — adjust filters or the field." : "Paste a URL, upload a file or bind a dataset field in the properties panel."}</div>
        </div>
      </div>
    );
  }

  const hover = img.hover || "none";
  const zoom = Math.max(100, Number(img.zoom) || 100) / 100;
  const scale = zoom * (hov && hover === "zoom" ? 1.06 : 1);
  const posX = img.posX ?? 50, posY = img.posY ?? 50;
  const baseOpacity = img.opacity ?? 1;

  return (
    <div data-testid="image-widget" onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={hasAction ? runAction : undefined} role={hasAction ? "link" : undefined}
      title={hasAction && action.type === "url" ? action.url : undefined}
      className={"h-full w-full overflow-hidden " + (hasAction ? "cursor-pointer " : "")}
      style={{
        borderRadius: img.radius ?? 0,
        transition: "box-shadow .3s ease, transform .3s ease",
        ...(hover === "lift" && hov ? { boxShadow: "0 10px 28px rgba(20,30,70,.22)", transform: "translateY(-3px)" } : {}),
      }}>
      <img data-testid="image-widget-img" src={url} alt={img.alt || ""} draggable={false}
        className="w-full h-full select-none"
        style={{
          objectFit: img.fit || "contain",
          objectPosition: `${posX}% ${posY}%`,
          opacity: hover === "fade" ? (hov ? 1 : Math.min(baseOpacity, 0.72)) : baseOpacity,
          filter: hover === "grayscale" ? (hov ? "grayscale(0)" : "grayscale(1)") : undefined,
          transform: scale !== 1 ? `scale(${scale})` : undefined,
          transformOrigin: `${posX}% ${posY}%`,
          transition: "transform .35s ease, filter .35s ease, opacity .35s ease",
        }} />
    </div>
  );
}
